// Pure grid-render index helpers for the Planner (D6, R5).
//
// These replace per-cell full-array scans in the render loop with precomputed
// indices that the component memoizes once per data change. Each function is
// pure and side-effect free so its output can be asserted equivalent to the
// previous per-cell logic (see plannerGrid.test files).

import { format, addMinutes, parseISO } from 'date-fns';

// --- 5.1.3 — shared per-booking temporal precompute -------------------------
//
// One parseISO per booking per bookings-change. Everything downstream (both
// indices + the booking-node render) reuses these numeric ms and HH:mm labels
// instead of re-parsing. Two ends are derived by cheap arithmetic because the
// duration semantics differ between call sites:
//   - endMsCell  uses booking.durationMinutes            (getBookingsForCell / node)
//   - endMsBlock uses durationMinutes ?? SLOT*slotsCount (getAdminBlockedReason)
// Numeric comparisons are bit-identical to the previous `Date < Date` (Date
// coerces via getTime); NaN edges (e.g. missing durationMinutes on the cell
// side) are preserved. Labels are guarded so an Invalid Date can never turn an
// eager precompute into a throw the old lazy render would not have hit for a
// booking that is never displayed.
export function buildBookingTemporals(bookings, slotDuration) {
  const map = new Map();
  for (const b of bookings) {
    const start = parseISO(b.startDateTime);
    const startMs = start.getTime();
    const endCell = addMinutes(start, b.durationMinutes);
    const blockDuration = b.durationMinutes != null
      ? Number(b.durationMinutes)
      : (slotDuration * Number(b.slotsCount || 1));
    const endBlock = addMinutes(start, blockDuration);
    map.set(b.id, {
      startMs,
      endMsCell: endCell.getTime(),
      endMsBlock: endBlock.getTime(),
      startLabel: Number.isNaN(startMs) ? '' : format(start, 'HH:mm'),
      endLabelCell: Number.isNaN(endCell.getTime()) ? '' : format(endCell, 'HH:mm'),
    });
  }
  return map;
}

// --- 5.1.1 — getBookingsForCell index --------------------------------------

// Build a Map "HH:mm|calendarId" -> bookings[] overlapping that slot, using the
// shared temporals. Semantics preserved bit-for-bit: exclude CANCELLED, overlap
// is `start < slotEnd && end > time` (now numeric), per-cell order matches
// insertion order of bookingsByCalendar.
export function buildBookingsByCellKey(bookingsByCalendar, timeSlots, temporals, slotDuration) {
  const map = new Map();
  for (const time of timeSlots) {
    const key = format(time, 'HH:mm');
    const timeMs = time.getTime();
    const slotEndMs = addMinutes(time, slotDuration).getTime();
    for (const calendarId of Object.keys(bookingsByCalendar)) {
      const cell = [];
      for (const booking of bookingsByCalendar[calendarId]) {
        if (booking.status === 'CANCELLED') continue;
        const t = temporals.get(booking.id);
        if (!t) continue;
        if (t.startMs < slotEndMs && t.endMsCell > timeMs) cell.push(booking);
      }
      if (cell.length) map.set(`${key}|${calendarId}`, cell);
    }
  }
  return map;
}

// O(1) lookup replacing getBookingsForCell. `time` must be a grid slot instant
// (both callers pass a value from `timeSlots`), so the HH:mm key matches.
export function lookupBookingsForCell(bookingsByCellKey, time, calendarId) {
  return bookingsByCellKey.get(`${format(time, 'HH:mm')}|${calendarId}`) || [];
}

// Planning-variant visual contracts. These helpers deliberately operate on
// the service catalog rather than changing the cell index key: HH:mm|calendarId
// remains the parent cell identity for capacity, click, drag/drop and pins.
export function findBookingVariant(service, booking) {
  if (booking?.variantId == null) return null;
  return (service?.variants || []).find(v => v.id === booking.variantId) || null;
}

export function resolveBookingColor(service, booking) {
  if (service?.enablePlanningVariants !== true) return service?.color;
  if (booking?.variantId == null) return service?.color;
  const variant = findBookingVariant(service, booking);
  return variant ? (variant.color || service?.color) : service?.color;
}

// 6.5 (D8): resolveLaneHeaderColor is the same D2 precedence, renamed so
// the call site doesn't lie: it is consumed ONLY by the persistent variant
// column header in Day Planner (R9). Booking cards keep their functional
// colors via getStatusClass — they no longer call this helper. The legacy
// name `resolveBookingColor` is preserved as a thin alias for backward
// compatibility with existing helpers/tests that haven't migrated yet;
// remove the alias once every consumer has been updated to
// `resolveLaneHeaderColor`.
export const resolveLaneHeaderColor = resolveBookingColor;

export function shouldRenderVariantLanes(service) {
  return service?.enablePlanningVariants === true
    && (service?.variants || []).filter(v => v.active !== false).length >= 2;
}

// D6 (R5): returns whether a sublane still has room for a new booking.
//
// `bookingsInCell` is the FULL bookings list already grouped at the parent
// cell (HH:mm|calendarId) — typically the array returned by
// `lookupBookingsForCell` / `buildBookingsByCellKey`. The helper does NOT
// filter by startDateTime or calendarId: the slot start and calendarId are
// caller-determined at render time in Planner.js and adding them here would
// couple this helper to the renderer's cell key. We only filter by
// `booking.variantId` so the helper stays pure and deterministic.
//
// `targetVariantId`:
//   - `null` (or absent) → the legacy/null bucket (count bookings whose
//     `variantId == null`).
//   - any other value → count bookings whose `variantId` matches it
//     exactly.
//
// The helper receives `targetVariantId` DIRECTLY (R5 / 2.7) so callers
// that hold an orphan / unknown / historical-unresolvable lane
// (`lane.variant === null`, `lane.variantId !== null`) can simply NOT call
//     the helper and therefore NOT collapse that lane into the legacy/null
// bucket. The previous `variant?.id ?? null` shape caused such lanes to
// silently count against the null bucket, which is exactly what R5.1
// forbids: only the legacy/null lane uses the null bucket; lanes that do
// not resolve to a known active catalog variant never do.
//
// Capacity rule (D6): the effective capacity of ANY lane (variant or
// legacy/null) is `service.capacityPerSlot` of the master service. Variants
// do NOT carry a configurable capacity and the frontend SHALL NOT compute,
// fall back on, or rely on any per-variant capacity override.
export function laneHasRemainingCapacity(service, targetVariantId, bookingsInCell) {
  const capacity = service?.capacityPerSlot ?? 1;
  const targetId = targetVariantId ?? null;
  const bookings = Array.isArray(bookingsInCell) ? bookingsInCell : [];
  const count = bookings.filter((b) => (b?.variantId ?? null) === targetId).length;
  return count < capacity;
}

// Returns stable lane descriptors for one parent cell. Active catalog entries
// always define the lane order, including lanes with no booking in this slot.
// Inactive catalog entries and unknown variant IDs are appended only when a
// booking in this cell needs them; null is the legacy lane and has no label.
export function buildVariantLanes(service, bookings) {
  if (!shouldRenderVariantLanes(service)) return [];

  const catalog = service?.variants || [];
  const active = catalog.filter(v => v.active !== false);
  const byKey = new Map();
  const lanes = active.map(variant => {
    const lane = { key: variant.id, variantId: variant.id, variant, bookings: [] };
    byKey.set(variant.id, lane);
    return lane;
  });

  let legacyLane = null;
  for (const booking of bookings || []) {
    if (booking?.variantId == null) {
      if (!legacyLane) {
        legacyLane = { key: '__legacy__', variantId: null, variant: null, bookings: [] };
        lanes.push(legacyLane);
      }
      legacyLane.bookings.push(booking);
      continue;
    }

    const existing = byKey.get(booking.variantId);
    if (existing) {
      existing.bookings.push(booking);
      continue;
    }

    const variant = catalog.find(v => v.id === booking.variantId) || null;
    if (variant) {
      if (!byKey.has(variant.id)) {
        const lane = { key: variant.id, variantId: variant.id, variant, bookings: [] };
        byKey.set(variant.id, lane);
        lanes.push(lane);
      }
      byKey.get(variant.id).bookings.push(booking);
    } else {
      const key = `__unknown__${String(booking.variantId)}`;
      if (!byKey.has(key)) {
        const lane = { key, variantId: booking.variantId, variant: null, bookings: [] };
        byKey.set(key, lane);
        lanes.push(lane);
      }
      byKey.get(key).bookings.push(booking);
    }
  }

  // Catalog order is already deterministic for active/inactive entries. Keep
  // unresolved IDs deterministic too, independent of booking arrival order.
  const known = lanes.filter(lane => lane.variant);
  const legacy = lanes.filter(lane => lane.variantId == null);
  const unknown = lanes
    .filter(lane => !lane.variant && lane.variantId != null)
    .sort((a, b) => String(a.variantId).localeCompare(String(b.variantId)));
  return [...known, ...unknown, ...legacy];
}

// 6.1 (D7) — buildServiceLaneColumns. Returns the column descriptors for a
// service FOR THE WHOLE DAY, not per slot. This is the persistent variant
// column set that the header emits once and every time row aligns with.
//
// Contract (D7.3, D7.5, R6.4):
//   - Active catalog variants always define the column order, in
//     deterministic catalog order, including columns with no booking of
//     the day (so columns do NOT shift between time rows).
//   - Legacy/null, inactive-with-history and unknown columns are appended
//     ONLY if some booking of the day requires them. They do not appear
//     for empty slots or empty days.
//   - No invented metadata: legacy/null has no `variant`; unknown has no
//     `variant`. Inactive-with-history keeps the catalog metadata so the
//     header can still label the column if needed (the booking stays
//     resolvable via D2).
//   - The shape is: { key, variantId, variant, isLegacy, isUnknown,
//     isInactive } — `bookings` is NOT included here; the per-slot
//     distribution lives in `buildVariantLanes`.
//
// `dayBookings` is the full day booking list for the service (all slots),
// typically the result of grouping bookingsByCalendar[service.id] by date.
// The function does NOT filter by slot or start time — the caller supplies
// the day-scoped set.
export function buildServiceLaneColumns(service, dayBookings) {
  if (!shouldRenderVariantLanes(service)) return [];

  const catalog = service?.variants || [];
  const active = catalog.filter(v => v.active !== false);

  const columns = active.map(variant => ({
    key: variant.id,
    variantId: variant.id,
    variant,
    isLegacy: false,
    isUnknown: false,
    isInactive: false,
  }));

  // Track which optional columns are required by the day's bookings.
  let hasLegacy = false;
  const inactiveIds = new Set();
  const unknownIds = new Set();

  for (const booking of dayBookings || []) {
    if (booking?.variantId == null) {
      hasLegacy = true;
      continue;
    }
    const variant = catalog.find(v => v.id === booking.variantId);
    if (variant && variant.active === false) {
      inactiveIds.add(variant.id);
      continue;
    }
    if (!variant) {
      unknownIds.add(booking.variantId);
    }
  }

  if (hasLegacy) {
    columns.push({
      key: '__legacy__',
      variantId: null,
      variant: null,
      isLegacy: true,
      isUnknown: false,
      isInactive: false,
    });
  }

  // Inactive-with-history in catalog order (deterministic).
  for (const variant of catalog) {
    if (variant.active === false && inactiveIds.has(variant.id)) {
      columns.push({
        key: variant.id,
        variantId: variant.id,
        variant,
        isLegacy: false,
        isUnknown: false,
        isInactive: true,
      });
    }
  }

  // Unknown ids, deterministic (lexicographic) regardless of arrival order.
  const sortedUnknown = Array.from(unknownIds).sort((a, b) =>
    String(a).localeCompare(String(b))
  );
  for (const id of sortedUnknown) {
    columns.push({
      key: `__unknown__${String(id)}`,
      variantId: id,
      variant: null,
      isLegacy: false,
      isUnknown: true,
      isInactive: false,
    });
  }

  return columns;
}

// 6.1 (D7.3) — per-slot distribution into the day's columns.
//
// `columns` is the result of `buildServiceLaneColumns` for the same service
// (and ideally the same day). When `columns` is omitted, the function
// derives them from `bookings` only — preserving the legacy per-slot
// behavior used by existing tests that call `buildVariantLanes(service,
// bookings)` directly. The persistent-column layout in `Planner.js` always
// passes `columns` from a parent-level `useMemo` so the column set does
// NOT shift between rows.
//
// Contract: this function no longer decides WHICH columns exist — it only
// distributes the slot's bookings into the columns that were already
// declared at the service+day level. Bookings whose lane has no matching
// column are dropped from the rendered distribution (they still exist in
// the cell's full list for capacity/drop purposes; see Planner.js).
export function distributeBookingsIntoColumns(service, bookings, columns) {
  if (!columns) return buildVariantLanes(service, bookings);
  if (columns.length === 0) return [];

  const byKey = new Map();
  const lanes = columns.map(col => ({
    key: col.key,
    variantId: col.variantId,
    variant: col.variant,
    isLegacy: col.isLegacy,
    isUnknown: col.isUnknown,
    isInactive: col.isInactive,
    bookings: [],
  }));
  for (const lane of lanes) byKey.set(lane.key, lane);

  const catalog = service?.variants || [];
  for (const booking of bookings || []) {
    let key;
    if (booking?.variantId == null) {
      key = '__legacy__';
    } else {
      const v = catalog.find(c => c.id === booking.variantId);
      if (v && v.active === false) {
        key = v.id;
      } else if (v && v.active !== false) {
        key = v.id;
      } else {
        key = `__unknown__${String(booking.variantId)}`;
      }
    }
    const lane = byKey.get(key);
    if (lane) lane.bookings.push(booking);
  }

  return lanes;
}

// 8.2 (D11) — buildCellLaneColumns. Returns the column descriptors for a
// SINGLE parent cell in Week (NOT per day). Mirrors the Day helper's
// precedence but is cell-scoped: a cell with bookings in only ONE active
// variant produces ONE column, not the full active catalog.
//
// Contract (D11, R6.4):
//   - Active catalog variants that have at least one booking in the cell
//     appear in deterministic catalog order.
//   - Legacy/null, inactive-with-history and unknown columns are appended
//     ONLY if some booking of the cell requires them. They do not appear
//     when the cell has no such booking.
//   - Empty cell (`cellBookings` empty or undefined) → `[]`. The cell is
//     rendered with the single-cell legacy path; no sub-cells.
//   - Feature OFF (or fewer than two active variants) → `[]`. No
//     sub-cells; the cell is rendered with the single-cell legacy path.
//   - No invented metadata: legacy/null has no `variant`; unknown has no
//     `variant`; inactive-with-history keeps the catalog metadata.
//
// `cellBookings` is the full booking list for the parent cell
// (HH:mm|calendarId) — typically the array returned by
// `bookingsByDaySlot[${dayKey}|${slot}]`. The function does NOT filter
// by date or service — the caller supplies the cell-scoped set.
//
// Compared to `buildServiceLaneColumns` (which is day-scoped and always
// returns the active catalog so columns don't shift between slots), this
// helper is cell-scoped so a Week cell only renders the sub-cells it
// actually needs — important under narrow width (D11 / 8.4 24px defense).
export function buildCellLaneColumns(service, cellBookings) {
  if (!shouldRenderVariantLanes(service)) return [];
  const bookings = Array.isArray(cellBookings) ? cellBookings : [];
  if (bookings.length === 0) return [];

  const catalog = service?.variants || [];
  const active = catalog.filter(v => v.active !== false);

  // Track which optional columns are required by the cell's bookings.
  const usedActiveIds = new Set();
  let hasLegacy = false;
  const inactiveIds = new Set();
  const unknownIds = new Set();

  for (const booking of bookings) {
    if (booking?.variantId == null) {
      hasLegacy = true;
      continue;
    }
    const variant = catalog.find(v => v.id === booking.variantId);
    if (variant && variant.active === false) {
      inactiveIds.add(variant.id);
      continue;
    }
    if (variant && variant.active !== false) {
      usedActiveIds.add(variant.id);
      continue;
    }
    unknownIds.add(booking.variantId);
  }

  // Active columns in deterministic catalog order, filtered to those that
  // actually appear in this cell (the cell-scoped invariant).
  const columns = active
    .filter(v => usedActiveIds.has(v.id))
    .map(variant => ({
      key: variant.id,
      variantId: variant.id,
      variant,
      isLegacy: false,
      isUnknown: false,
      isInactive: false,
    }));

  if (hasLegacy) {
    columns.push({
      key: '__legacy__',
      variantId: null,
      variant: null,
      isLegacy: true,
      isUnknown: false,
      isInactive: false,
    });
  }

  // Inactive-with-history in catalog order (deterministic).
  for (const variant of catalog) {
    if (variant.active === false && inactiveIds.has(variant.id)) {
      columns.push({
        key: variant.id,
        variantId: variant.id,
        variant,
        isLegacy: false,
        isUnknown: false,
        isInactive: true,
      });
    }
  }

  // Unknown ids, deterministic (lexicographic) regardless of arrival order.
  const sortedUnknown = Array.from(unknownIds).sort((a, b) =>
    String(a).localeCompare(String(b))
  );
  for (const id of sortedUnknown) {
    columns.push({
      key: `__unknown__${String(id)}`,
      variantId: id,
      variant: null,
      isLegacy: false,
      isUnknown: true,
      isInactive: false,
    });
  }

  return columns;
}

// --- 5.1.4 — D5 group pin lookup (R9 frontend) -----------------------------
//
// Returns the staffId that the (calendarId, variantId, startDateTime) group
// is currently pinned to, or null if the group has no pin yet. The first
// non-cancelled booking in the cell with a matching variantId and a staffId
// is treated as the canonical pin (the backend enforces uniqueness via
// db.variant_pins; this is a read-side mirror for the UI).
export function resolveGroupPin(bookingsByCellKey, time, calendarId, variantId) {
  if (!variantId) return null;
  const cell = bookingsByCellKey.get(`${format(time, 'HH:mm')}|${calendarId}`) || [];
  for (const b of cell) {
    if (b.variantId === variantId && b.staffId && b.status !== 'CANCELLED' && b.status !== 'NO_SHOW') {
      return b.staffId;
    }
  }
  return null;
}

// --- 5.1.2 — getAdminBlockedReason indices (D6 extension) -------------------

// compatibleStaffByService: calendarId -> staff[] serving it. Replaces the
// per-cell `staffUsers.filter(...)`. Built for every known service so the query
// never falls back to a scan.
export function buildCompatibleStaffByService(staffUsers, services) {
  const map = {};
  for (const service of services) {
    map[service.id] = staffUsers.filter(s => (s.serviceIds || []).includes(service.id));
  }
  return map;
}

// blockingBookingsByStaff: staffId -> [{ calendarId, startMs, endMs }] for the
// cell-INDEPENDENT filters of getAdminBlockedReason (has staffId, not
// placeholder, status not CANCELLED/NO_SHOW). Uses the shared temporals'
// blocking end, so each cell scans only that staff's bookings, numerically.
export function buildBlockingBookingsByStaff(bookings, temporals) {
  const map = {};
  for (const b of bookings) {
    if (!b.staffId || b.isPlaceholder) continue;
    if (b.status === 'CANCELLED' || b.status === 'NO_SHOW') continue;
    const t = temporals.get(b.id);
    if (!t) continue;
    if (!map[b.staffId]) map[b.staffId] = [];
    map[b.staffId].push({ calendarId: b.calendarId, startMs: t.startMs, endMs: t.endMsBlock });
  }
  return map;
}

// Body of getAdminBlockedReason, cell-DEPENDENT filters kept intact:
// `iv.calendarId !== calendarId` (a same-service booking is counted by
// capacity/isFull, not a block) and the overlap `start < slotEnd && end > time`
// (numeric) with slotEnd = time + slotsPerBooking * slotDuration. The caller
// keeps the isClient / staffUsers.length / service-exists guards.
export function computeAdminBlockedReason({
  compatibleStaffByService,
  blockingBookingsByStaff,
  service,
  time,
  calendarId,
  slotDuration,
}) {
  const compatibleStaff = compatibleStaffByService[calendarId] || [];
  if (compatibleStaff.length === 0) return 'NO_STAFF_AVAILABLE';
  const timeMs = time.getTime();
  const slotEndMs = addMinutes(time, (service.slotsPerBooking || 2) * slotDuration).getTime();
  const anyFree = compatibleStaff.some(staff => {
    const intervals = blockingBookingsByStaff[staff.id] || [];
    return !intervals.some(iv =>
      iv.calendarId !== calendarId && iv.startMs < slotEndMs && iv.endMs > timeMs
    );
  });
  return anyFree ? null : 'NO_STAFF_AVAILABLE';
}
